import type { VabiloZaPrikaz } from '@koda/shared';
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { useBesedila, useJezik } from '../lib/jezik';

interface Lastnosti {
  /** Poklicano po sprejemu vabila; uporabnik je takrat že prijavljen. */
  naSprejem: () => void;
  /**
   * `vabilo`: povabljeni si prvič nastavi geslo. `ponastavitev`: novo geslo po
   * povezavi, ki jo pošlje skrbnik; ob shranjevanju padejo vse druge seje.
   */
  vrsta?: 'vabilo' | 'ponastavitev';
}

/**
 * Sprejem vabila ali novo geslo po povezavi od skrbnika: uporabnik si nastavi
 * geslo in je prijavljen.
 *
 * Stran je dosegljiva brez prijave. Žeton se porabi šele ob nastavitvi gesla,
 * zato osvežitev strani povezave ne uniči.
 */
export function Vabilo({ naSprejem, vrsta = 'vabilo' }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const jezik = useJezik();
  const { zeton = '' } = useParams();
  const pojdi = useNavigate();
  const [vabilo, setVabilo] = useState<VabiloZaPrikaz | null>(null);
  const [berem, setBerem] = useState(true);
  const [geslo, setGeslo] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);

  const ponastavitev = vrsta === 'ponastavitev';

  useEffect(() => {
    (ponastavitev ? api.ponastavitev(zeton) : api.vabilo(zeton))
      .then(setVabilo)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : ponastavitev ? b.ponastavitveNiMogoce : b.vabilaNiMogoce);
      })
      .finally(() => {
        setBerem(false);
      });
  }, [zeton, ponastavitev]);

  const oddaj = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    setShranjujem(true);
    setNapaka(null);
    (ponastavitev ? api.nastaviNovoGeslo(zeton, geslo) : api.sprejmiVabilo(zeton, geslo, jezik))
      .then(() => {
        naSprejem();
        // Porabljene povezave ni vec smiselno prikazovati; uporabnik je prijavljen.
        pojdi('/', { replace: true });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.gesloNiMogoce);
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  return (
    <main className="prijava">
      <h1>{b.znamka}</h1>
      <p className="podnaslov">{b.podnaslov}</p>

      <section className="kartica">
        <h2>{ponastavitev ? b.novoGesloNaslov : b.nastavitevGesla}</h2>
        {berem && <p>{b.berem}</p>}
        {!berem && vabilo === null && (
          <p className="napaka">{napaka ?? (ponastavitev ? b.ponastavitevNiVeljavna : b.vabiloNiVeljavno)}</p>
        )}
        {vabilo !== null && (
          <>
            <p className="drobno">{ponastavitev ? b.ponastavitevZa(vabilo.email) : b.vabiloZa(vabilo.email)}</p>
            <form onSubmit={oddaj} className="obrazec">
              <label>
                {b.novoGeslo}
                <input
                  type="password"
                  value={geslo}
                  autoComplete="new-password"
                  minLength={10}
                  onChange={(d) => {
                    setGeslo(d.target.value);
                  }}
                  required
                  autoFocus
                />
              </label>
              {napaka !== null && <p className="napaka">{napaka}</p>}
              <button type="submit" disabled={shranjujem || geslo.length < 10}>
                {shranjujem ? b.shranjujem : ponastavitev ? b.shraniNovoGeslo : b.nastaviGeslo}
              </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
